<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router';
import { productLPLinks } from '~/components/templates/ProductLpTemplate/productLPLinks';
import PillShapedSpan from '~theme/components/atoms/PillShapedSpan.vue';

export type MediaProps = {
  location: RouteLocationRaw;
  imageSrc: string;
  relatedArticleLinks: {
    title: string;
    location: RouteLocationRaw;
  }[];
};

defineProps<{
  keyword: string;
  media?: MediaProps;
}>();

const route = useRoute();
const currentCanonicalPath = route.path.replace(/\/+$/, '');
const otherProudctLPLinks = Object.values(productLPLinks).filter(
  (l) => l.location !== currentCanonicalPath
);
</script>

<template>
  <div v-if="!!media" :class="$style.container">
    <div :class="$style.section">
      <div :class="$style.section__text">
        <p :class="$style.section__heading">
          {{ keyword }}のことがわかる！オカネコマガジン
        </p>
        <p :class="$style.section__body">
          オカネコでは{{
            keyword
          }}のやり方や、相談方法について知ることができるオカネコマガジンを運営しています。どこに相談するべきか、何から始めるべきかを調べることにより、より精度の高いアドバイスを受けられますので、ぜひチェックしてみてください。
        </p>
      </div>
      <NuxtLink external target="_blank" :href="media.location">
        <img
          :class="$style.section__image"
          :src="media.imageSrc"
          loading="lazy"
        />
      </NuxtLink>
    </div>
    <div
      v-if="media.relatedArticleLinks.length > 0"
      :class="$style.relatedArticleSection"
    >
      <p :class="$style.relatedArticleSection__heading">
        {{ keyword }}についての記事
      </p>
      <div
        :class="[
          $style.relatedArticleSection__links,
          $style.relatedArticleSectionLinks,
        ]"
      >
        <NuxtLink
          v-for="a in media.relatedArticleLinks"
          :key="a.title"
          :href="a.location"
          :class="$style.relatedArticleSectionLinks__link"
        >
          {{ a.title }}
        </NuxtLink>
      </div>
    </div>
    <div :class="$style.otherConsultationSection">
      <p :class="$style.otherConsultationSection__heading">
        ほかの相談内容を見る
      </p>
      <div
        :class="[
          $style.otherConsultationSection__links,
          $style.otherConsultationSectionLinks,
        ]"
      >
        <NuxtLink
          v-for="l in otherProudctLPLinks"
          :key="l.label"
          :href="l.location"
        >
          <PillShapedSpan :class="$style.otherConsultationSectionLinks__tag">
            {{ l.label }}
          </PillShapedSpan>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use './_constants.scss' as *;
.container {
  padding: 0 16px;
  margin: auto;
  max-width: $content-width;
}

.section {
  display: flex;
  line-height: 1.8em;
  gap: 24px;
  align-items: center;

  @include mobile-only {
    flex-direction: column-reverse;
    gap: 16px;
  }

  &__text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: center;
  }
  &__heading {
    font-size: 24px;
    font-weight: bold;
  }
  &__body {
    font-size: 16px;
  }
  &__image {
    width: 380px;
    max-width: 380px;
    box-sizing: border-box;

    border: 1px solid $color-border-low-emphasis;
    border-radius: 8px;

    @include mobile-only {
      width: 100%;
      height: auto;
    }
  }
}

.relatedArticleSection {
  margin-top: 40px;
  @include mobile-only {
    margin-top: 32px;
  }

  &__heading {
    font-size: 20px;
    line-height: 36px;
    font-weight: bold;
  }

  &__links {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px; /* tmp */
    font-size: 16px;
    line-height: 28.8px;
  }
}

.relatedArticleSectionLinks {
  color: $color-text-and-object-accent-primary;
  text-decoration: underline;
}

.relatedArticleSectionLinks__link:not(:last-child) {
  border-bottom: 1px solid $color-border-low-emphasis;
}

.otherConsultationSection {
  margin-top: 24px;

  &__heading {
    font-size: 20px;
    line-height: 36px;
    font-weight: bold;
  }

  &__links {
    margin-top: 8px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.otherConsultationSectionLinks {
  &__tag {
    padding: 4px 12px;
    font-size: 12px;
    line-height: 21.6px;
    font-weight: bold;
    background-color: $color-surface-primary;
  }
}
</style>
